import { Injectable, InjectionToken, Injector, inject } from '@angular/core';
import { AppCheck } from '@angular/fire/app-check';
import { ToastController } from '@ionic/angular';
import { getToken } from 'firebase/app-check';
import { OfflineDeviceKind } from '@koomzo/commonmodel';
import { Subscription } from 'rxjs';
import { environment } from '@env/environment';

import type { BackendProvider } from '../../types';
import { kzDetectPlatform } from '../../../platform-capability.service';
import { KzLocaleService } from '../../../locale/kz-locale.service';
import { BACKEND_PROVIDER } from '../../tokens';
import type { DeviceRegistration, DeviceRegistrationState } from './device-identity.model';
import type { DeviceIdentityService } from './device-identity.service';

/**
 * kzd-ac AC-3, ruling AC-R1 (a) "an owner signs in once": every desktop registers, so it can be minted App Check
 * tokens (app-check-desktop.ts mints only for a registered device key).
 *
 * **Dormant until the env turns desktop App Check on** (`environment.appCheck.desktop`, wave-2 review M1/L3): off —
 * every env until the owner registers the desktop app and sets `APP_CHECK_DESKTOP_APP_ID` — nothing at all happens
 * here (no registration, no staff notice), exactly as before AC-3.
 *
 * On, on the Electron desktop, on the Firebase backend, once a business is signed in:
 * - **an owner or a manager**, and the SERVER's answer to their live read of `devices` says this desktop is not
 *   registered in that business (`unregistered`, neither the kept record nor Firestore's cache — review L2) → it is
 *   registered at once, through the existing flow (`DeviceIdentityService.register`, `registerDevice` — App Check
 *   exempt for this bootstrap), with no dialog: named `Desktop <code>` (`desktopDeviceName`: the short end of its
 *   device id, so a business's list is not a column of identical names — review L3; the app exposes no OS host name
 *   to the page), kind `till` (the Devices page's default). Once per business per session, and fail open: offline,
 *   refused, or any limit simply leaves it for the next start; nothing waits for it. While the Devices page's own
 *   Register is on its way (`DeviceIdentityService.registering`), it yields: that one decides the name.
 * - **only on that definite answer** (wave-2 glue): an `unknown` standing, a read that failed, or a kept `unregistered`
 *   (the read failed, so the device fell back to its last answer) → nothing; the next start reads again. The server's
 *   `registerDevice` revives a revoked device, so registering on a guess could undo a manager's revoke the device has
 *   not heard of yet.
 * - **a token at once** (wave-2 glue): once registered, App Check is asked for a token there and then (its public
 *   `getToken`), so the desktop's first token does not wait for the SDK's next request or its refresher's back-off.
 *   Fail open; without App Check started, nothing.
 * - **staff**, and it is not registered → one short toast per session: ask an owner or a manager to sign in here once.
 * - **registered**, or **revoked** (a manager's deliberate act — never undone behind their back) → nothing.
 * Never on the web, Android or iOS, and never under the mock: then nothing is loaded at all — the identity service and
 * its key store are imported only on the desktop (they stay out of the initial bundle, as `DeviceRegistrationKept`
 * notes).
 */

/** The name a desktop registers under (ruling AC-R1 a). */
export const KZ_DESKTOP_DEVICE_NAME = 'Desktop';
/** Its kind: the Devices page's default, a till. */
export const KZ_DESKTOP_DEVICE_KIND: OfflineDeviceKind = 'till';
/** The staff notice (`app.pages.devices.desktopNotRegistered`). */
export const KZ_DESKTOP_NOT_REGISTERED_KEY = 'app.pages.devices.desktopNotRegistered';

/**
 * `environment.appCheck.desktop` (wave-2 review M1): only when it is on does a desktop register itself or tell staff
 * it is not registered. A token, so a spec turns it on.
 */
export const KZ_DESKTOP_APP_CHECK_ON = new InjectionToken<boolean>('KZ_DESKTOP_APP_CHECK_ON', {
  providedIn: 'root',
  factory: () => (environment.appCheck as { desktop?: boolean }).desktop === true,
});

/**
 * The name a desktop registers itself under (review L3): `Desktop` and the last four letters or digits of its device
 * id, upper-cased (`dev-mh2k9x7q` → `Desktop 9X7Q`) — short enough to read in the Devices list, and
 * different from one desktop to the next. The device id is the one thing it knows before it is registered (its
 * short code `D<n>` comes back from the registration).
 */
export function desktopDeviceName(deviceId: string): string {
  const tail = deviceId.replace(/[^A-Za-z0-9]/g, '').slice(-4).toUpperCase();
  return tail ? `${KZ_DESKTOP_DEVICE_NAME} ${tail}` : KZ_DESKTOP_DEVICE_NAME;
}

/** The App Check call made right after registering (a seam for the spec): the SDK's public `getToken`. */
export const KZ_DESKTOP_FIRST_TOKEN = {
  getToken: (appCheck: AppCheck): Promise<unknown> => getToken(appCheck, false),
};

/** What this desktop does about its registration, for the signed-in person (see the file comment). */
export type DesktopRegistrationStep = 'register' | 'notice' | 'none';

export interface DesktopRegistrationInput {
  readonly platform: string;
  readonly backend: BackendProvider;
  readonly tenantId: string;
  readonly registrar: boolean;
  readonly state: DeviceRegistrationState;
  /** The standing is the device's kept record (staff, or a read that failed), not a live read (`DeviceRegistration`). */
  readonly fromCache: boolean;
}

/**
 * The rule (pure, for the spec): register for an owner or a manager on a live "not registered" only, a notice for
 * staff, else nothing.
 */
export function desktopRegistrationStep(i: DesktopRegistrationInput): DesktopRegistrationStep {
  if (i.platform !== 'electron' || i.backend !== 'firebase' || !i.tenantId) return 'none';
  if (i.state === 'registered' || i.state === 'revoked') return 'none';
  if (!i.registrar) return 'notice';
  return i.state === 'unregistered' && !i.fromCache ? 'register' : 'none';
}

/** The identity service as this one uses it (a seam for the spec). */
export type DesktopRegistrationIdentity = Pick<DeviceIdentityService, 'registration$' | 'isRegistrar' | 'register'> &
  Partial<Pick<DeviceIdentityService, 'registering'>>;

@Injectable({ providedIn: 'root' })
export class DesktopRegistrationService {
  private readonly injector = inject(Injector);
  private readonly backend = inject(BACKEND_PROVIDER);
  private readonly desktopOn = inject(KZ_DESKTOP_APP_CHECK_ON);

  /** Businesses this session already tried to register in (once each; the next start tries again). */
  private readonly tried = new Set<string>();
  private noticed = false;
  private sub: Subscription | null = null;

  /**
   * Started at start-up (app.module, APP_INITIALIZER), never awaited. Off the desktop, off the Firebase backend, or
   * with desktop App Check off (review M1) it does nothing at all. `identity` is a seam for the spec; by default the
   * identity service is loaded on demand.
   */
  async start(platform: string = kzDetectPlatform(), identity?: DesktopRegistrationIdentity): Promise<void> {
    if (this.sub || platform !== 'electron' || this.backend !== 'firebase' || !this.desktopOn) return;
    try {
      const id = identity ?? this.injector.get((await import('./device-identity.service')).DeviceIdentityService);
      this.sub = id.registration$.subscribe((r) => void this.onRegistration(platform, id, r));
    } catch (e) {
      console.warn('[kz] Desktop registration did not start.', e);
    }
  }

  stop(): void {
    this.sub?.unsubscribe();
    this.sub = null;
  }

  /** A token now, for the device just registered (see the file comment). Never throws. */
  private async firstToken(): Promise<void> {
    try {
      const appCheck = this.injector.get(AppCheck, null);
      if (appCheck) await KZ_DESKTOP_FIRST_TOKEN.getToken(appCheck);
    } catch {
      /* the SDK's next request asks again */
    }
  }

  private async onRegistration(platform: string, id: DesktopRegistrationIdentity, r: DeviceRegistration): Promise<void> {
    const step = desktopRegistrationStep({
      platform,
      backend: this.backend,
      tenantId: r.tenantId,
      registrar: id.isRegistrar(),
      state: r.state,
      fromCache: r.fromCache,
    });
    if (step === 'register') {
      if (this.tried.has(r.tenantId)) return;
      /* Review L3: the Devices page is registering this device right now — it wins; its answer comes through here. */
      if (id.registering?.()) return;
      this.tried.add(r.tenantId);
      const out = await id.register(desktopDeviceName(r.deviceId), KZ_DESKTOP_DEVICE_KIND);
      console.info(out.ok ? '[kz] This desktop is now a registered device.' : `[kz] This desktop was not registered (${out.reason}); the next start tries again.`);
      if (out.ok) await this.firstToken();
    } else if (step === 'notice' && !this.noticed) {
      this.noticed = true;
      try {
        const message = this.injector.get(KzLocaleService).translate(KZ_DESKTOP_NOT_REGISTERED_KEY);
        const toast = await this.injector.get(ToastController).create({ message, duration: 6000, position: 'bottom', icon: 'information-circle-outline' });
        await toast.present();
      } catch {
        /* a notice is never worth an error */
      }
    }
  }
}
